// @file Dashboard store
// It wraps the dashboard Vuex store into a Pinia store.
import type { PopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type {
  AccountKey,
  Folder,
  FolderId,
  Library,
  LibraryId,
  OwnerOnboardingTask,
  OwnerOnboardingTaskData,
  User,
  UserGroup,
  UserGroupId,
  UserOptions,
  WallCamelCase as Wall,
  WallId,
} from '@@/types'
import type { AccountsMenuItem, DashView, MobilePage, NonCurrentTenantAccountsMenuItem } from '@@/types/dash'
import type { LibraryIdToUserRoleMap } from '@@/vuexstore/modules/accounts'
import { captureNonNetworkFetchError } from 'bits/error_tracker'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Store } from 'vuex'

export const useDashboardStore = defineStore('dashboard', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State
  const vuexStore = ref<Store<any> | undefined>(undefined)

  // Getters
  const isNativeAccount = computed((): boolean => vuexStore.value?.getters.isNativeAccount)
  const isBackpack = computed((): boolean => vuexStore.value?.getters.isBackpack)
  const isBriefcase = computed((): boolean => vuexStore.value?.getters.isBriefcase)
  const isStudent = computed((): boolean => vuexStore.value?.getters.isStudent)
  const currentTenantLogo = computed((): string => vuexStore.value?.getters.currentTenantLogo)
  const currentTenantName = computed((): string => vuexStore.value?.getters.currentTenantName)
  const currentUser = computed((): User => vuexStore.value?.getters.currentUser)
  const currentUserOptions = computed((): UserOptions => vuexStore.value?.getters.currentUserOptions)
  const wallViewableAndVisibleLibrariesArray = computed(
    (): Library[] => vuexStore.value?.getters['accounts/wallViewableAndVisibleLibrariesArray'],
  )
  const wallViewableLibrariesArray = computed(
    (): Library[] => vuexStore.value?.getters['accounts/wallViewableLibrariesArray'],
  )
  const wallCreatableLibraries = computed(
    (): Library[] => vuexStore.value?.getters['accounts/wallCreatableLibrariesArray'],
  )

  const userRolesByLibraryId = computed(
    (): LibraryIdToUserRoleMap => vuexStore.value?.getters['accounts/userRolesByLibraryId'],
  )
  const defaultLibrary = computed((): Library | undefined => vuexStore.value?.getters.defaultLibrary)

  const isActiveCollectionCrossLibrary = computed(
    (): boolean => vuexStore.value?.getters['collections/isActiveCollectionCrossLibrary'],
  )
  const currentAccountKey = computed((): AccountKey | null => vuexStore.value?.getters['collections/activeAccountKey'])
  const currentLibrary = computed((): Library | undefined => {
    if (currentAccountKey.value == null) return undefined
    if (currentAccountKey.value.type === 'user') return undefined
    return vuexStore.value?.getters['accounts/librariesById'][currentAccountKey.value.id]
  })

  const activeCollectionWalls = computed((): Wall[] => vuexStore.value?.getters['collections/activeCollectionWalls'])
  const activeUserGroups = computed((): UserGroup[] => vuexStore.value?.getters['collections/activeUserGroups'])
  const currentAccountName = computed((): string => vuexStore.value?.getters.currentAccountName)
  const xUserGroupsSection = computed((): boolean => vuexStore.value?.getters.xUserGroupsSection)
  const xSharedCollections = computed((): boolean => vuexStore.value?.getters.xSharedCollections)
  const currentUserAccountKey = computed((): AccountKey => vuexStore.value?.getters.currentUserAccountKey)
  const isActiveGroupId = computed(
    (): ((groupId: UserGroupId) => boolean) => vuexStore.value?.getters['collections/isActiveGroupId'],
  )
  const xGettingStarted = computed((): boolean => vuexStore.value?.getters.xGettingStarted)
  const xGettingStartedRow = computed((): boolean => vuexStore.value?.getters.xGettingStartedRow)
  const currentLibraryDashboardPath = computed((): string => vuexStore.value?.getters.currentLibraryDashboardPath)
  const nonCurrentTenantAccountsMenuList = computed(
    (): NonCurrentTenantAccountsMenuItem[] => vuexStore.value?.getters.nonCurrentTenantAccountsMenuList,
  )
  const accountsMenuList = computed((): AccountsMenuItem[] => vuexStore.value?.getters.accountsMenuList)
  const currentLibraryId = computed((): LibraryId | undefined => vuexStore.value?.getters.currentLibraryId)
  const currentLibraryOwnerOnboardingTasksData = computed(
    (): OwnerOnboardingTaskData[] => vuexStore.value?.getters.currentLibraryOwnerOnboardingTasksData,
  )
  const numOwnerOnboardingTasksCompleted = computed(
    (): number => vuexStore.value?.getters.numOwnerOnboardingTasksCompleted,
  )
  const ownerOnboardingTasksTotal = computed((): number => vuexStore.value?.getters.ownerOnboardingTasksTotal)
  const ownerOnboardingProgressString = computed((): string => vuexStore.value?.getters.ownerOnboardingProgressString)
  const ownerOnboardingTasksAllCompleted = computed(
    (): boolean => vuexStore.value?.getters.ownerOnboardingTasksAllCompleted,
  )
  const currentMobilePage = computed((): MobilePage => vuexStore.value?.getters.currentMobilePage)
  const isCurrentAccountLibrary = computed((): boolean => vuexStore.value?.getters.isCurrentAccountLibrary)
  const dashNameString = computed((): string => vuexStore.value?.getters.dashNameString)
  const dashGreeting = computed((): string => vuexStore.value?.getters.dashGreeting)
  const canViewCrossLibraryGallery = computed((): boolean => vuexStore.value?.getters.canViewCrossLibraryGallery)
  const folderActionsFolder = computed((): Folder | null => vuexStore.value?.getters.folderActionsFolder)
  const folderActionsAnchor = computed((): PopoverAnchor | null => vuexStore.value?.getters.folderActionsAnchor)
  const xFolders = computed((): boolean => vuexStore.value?.getters.xFolders)
  const isInitialized = computed((): boolean => vuexStore.value?.getters.isInitialized)
  const xAccountMore = computed((): boolean => vuexStore.value?.getters.xAccountMore)
  const accountMoreAnchor = computed((): PopoverAnchor | null => vuexStore.value?.getters.accountMoreAnchor)
  const wallActionsAnchor = computed((): PopoverAnchor | null => vuexStore.value?.getters.wallActionsAnchor)

  // Actions

  // The state here is used by a root component and getters would be called before the vuex store is available on the window object.
  // We would need to rely on this method to populate it instead of directly called getVuexStore.
  function initialize(options: { vuexStore: Store<any> | undefined }): void {
    vuexStore.value = options.vuexStore
  }

  function createWallAndRefetchData(payload: { layout: string; groupBySection?: boolean }): void {
    void vuexStore.value?.dispatch('createWallAndRefetchData', payload, { root: true })
  }

  function closeLayoutPicker(): void {
    void vuexStore.value?.dispatch('closeLayoutPicker', { root: true })
  }

  function switchView(dashView: DashView): void {
    void vuexStore.value?.dispatch('switchView', dashView, { root: true })
  }

  function promptNameForActiveUserNewFolder(): void {
    void vuexStore.value?.dispatch('promptNameForActiveUserNewFolder', { root: true })
  }

  function navigateToOwnerOnboardingTask(payload: { taskKey: OwnerOnboardingTask }): void {
    void vuexStore.value?.dispatch('navigateToOwnerOnboardingTask', payload, { root: true })
  }

  function showWallActions(payload: { wallId: WallId; popoverAnchor?: PopoverAnchor }): void {
    void vuexStore.value?.dispatch('showWallActions', payload, { root: true })
  }

  function showFolderActions(payload: { popoverAnchor: PopoverAnchor; folder: Folder }): void {
    void vuexStore.value?.dispatch('showFolderActions', payload, { root: true })
  }

  function confirmDismissOnboarding(): void {
    void vuexStore.value?.dispatch('confirmDismissOnboarding', { root: true })
  }

  function promptURLForJoinPadlet(): void {
    void vuexStore.value?.dispatch('promptURLForJoinPadlet', { root: true })
  }

  function createWallOrPromptUpgrade(): void {
    void vuexStore.value?.dispatch('createWallOrPromptUpgrade', { root: true })
  }

  function hideAccountMore(): void {
    void vuexStore.value?.dispatch('hideAccountMore', { root: true })
  }

  function closeFolderActions(): void {
    void vuexStore.value?.dispatch('closeFolderActions', { root: true })
  }

  function confirmDeleteFolder(payload: { folderId: FolderId }): void {
    void vuexStore.value?.dispatch('confirmDeleteFolder', payload, { root: true })
  }

  function promptRenameFolder(payload: { folderId: FolderId }): void {
    void vuexStore.value?.dispatch('promptRenameFolder', payload, { root: true })
  }

  function isCurrentAccount(accountKey: AccountKey): boolean {
    return vuexStore.value?.getters.isCurrentAccount(accountKey)
  }

  function showAccountMore(payload: { popoverAnchor: PopoverAnchor }): void {
    void vuexStore.value?.dispatch('showAccountMore', payload, { root: true })
  }

  function updateAccountMoreAnchor(payload: { popoverAnchor: PopoverAnchor }): void {
    void vuexStore.value?.dispatch('updateAccountMoreAnchor', payload, { root: true })
  }

  function closeBookmarksDialog(): void {
    void vuexStore.value?.dispatch('closeBookmarksDialog', { root: true })
  }

  function genericFetchError(payload: { error: any }): void {
    captureNonNetworkFetchError(payload.error)
    globalSnackbarStore.genericFetchError()
  }

  function hideWallActions(): void {
    void vuexStore.value?.dispatch('hideWallActions', { root: true })
  }

  function closeWallActions(): void {
    void vuexStore.value?.dispatch('closeWallActions', { root: true })
  }

  function copyActiveWallLinkToClipboard(): void {
    void vuexStore.value?.dispatch('copyActiveWallLinkToClipboard', { root: true })
  }

  function openActiveWallSlideshow(): void {
    void vuexStore.value?.dispatch('openActiveWallSlideshow', { root: true })
  }

  function archiveActiveWall(): void {
    void vuexStore.value?.dispatch('archiveActiveWall', { root: true })
  }

  function unarchiveActiveWallOrPromptUpgrade(): void {
    void vuexStore.value?.dispatch('unarchiveActiveWallOrPromptUpgrade', { root: true })
  }

  function confirmPermanentlyDeleteActiveWall(): void {
    void vuexStore.value?.dispatch('confirmPermanentlyDeleteActiveWall', { root: true })
  }

  function trashActiveWall(): void {
    void vuexStore.value?.dispatch('trashActiveWall', { root: true })
  }

  function untrashActiveWallOrPromptUpgrade(): void {
    void vuexStore.value?.dispatch('untrashActiveWallOrPromptUpgrade', { root: true })
  }

  function confirmLeaveActiveWall(): void {
    void vuexStore.value?.dispatch('confirmLeaveActiveWall', { root: true })
  }

  function openBookmarksDialog(): void {
    void vuexStore.value?.dispatch('openBookmarksDialog', { root: true })
  }

  function removeWallRecents(): void {
    void vuexStore.value?.dispatch('removeWallRecents', { root: true })
  }

  return {
    // State

    // Getters
    isNativeAccount,
    isBackpack,
    isBriefcase,
    isStudent,
    currentTenantLogo,
    currentTenantName,
    currentUser,
    currentUserOptions,
    wallViewableAndVisibleLibrariesArray,
    wallViewableLibrariesArray,
    wallCreatableLibraries,
    userRolesByLibraryId,
    defaultLibrary,
    isActiveCollectionCrossLibrary,
    currentAccountKey,
    activeCollectionWalls,
    activeUserGroups,
    isActiveGroupId,
    currentLibrary,
    currentAccountName,
    xUserGroupsSection,
    xSharedCollections,
    currentUserAccountKey,
    xGettingStarted,
    xGettingStartedRow,
    currentLibraryDashboardPath,
    nonCurrentTenantAccountsMenuList,
    currentLibraryId,
    currentLibraryOwnerOnboardingTasksData,
    numOwnerOnboardingTasksCompleted,
    ownerOnboardingTasksTotal,
    ownerOnboardingProgressString,
    ownerOnboardingTasksAllCompleted,
    currentMobilePage,
    isCurrentAccountLibrary,
    dashNameString,
    dashGreeting,
    canViewCrossLibraryGallery,
    accountsMenuList,
    folderActionsAnchor,
    folderActionsFolder,
    xFolders,
    isInitialized,
    xAccountMore,
    accountMoreAnchor,
    wallActionsAnchor,

    // Actions
    initialize,
    createWallAndRefetchData,
    closeLayoutPicker,
    switchView,
    promptNameForActiveUserNewFolder,
    navigateToOwnerOnboardingTask,
    showWallActions,
    showFolderActions,
    confirmDismissOnboarding,
    promptURLForJoinPadlet,
    createWallOrPromptUpgrade,
    hideAccountMore,
    closeFolderActions,
    confirmDeleteFolder,
    promptRenameFolder,
    isCurrentAccount,
    showAccountMore,
    updateAccountMoreAnchor,
    closeBookmarksDialog,
    genericFetchError,
    hideWallActions,
    closeWallActions,
    copyActiveWallLinkToClipboard,
    openActiveWallSlideshow,
    archiveActiveWall,
    unarchiveActiveWallOrPromptUpgrade,
    confirmPermanentlyDeleteActiveWall,
    trashActiveWall,
    untrashActiveWallOrPromptUpgrade,
    confirmLeaveActiveWall,
    openBookmarksDialog,
    removeWallRecents,
  }
})
